import { AgentsActions, IActionQueuesPayload } from "DistributedTaskControls/Actions/AgentsActions";
import { MessageHandlerActionsCreator } from "DistributedTaskControls/Actions/MessageHandlerActionsCreator";
import { ActionCreatorManager } from "DistributedTaskControls/Common/Actions/ActionCreatorManager";
import { ActionsHubManager } from "DistributedTaskControls/Common/Actions/ActionsHubManager";
import { ActionCreatorBase } from "DistributedTaskControls/Common/Actions/Base";
import { ActionCreatorKeys, MessageParentKeyConstants } from "DistributedTaskControls/Common/Common";
import { PhaseCache } from "DistributedTaskControls/Phase/PhaseCache";
import { IAgentSpecification } from "DistributedTaskControls/Phase/Types";
import { AgentsSource } from "DistributedTaskControls/Sources/AgentsSource";
import { MessageBarType } from "OfficeFabric/MessageBar";
import { TaskAgentQueue } from "TFS/DistributedTask/Contracts";

export class AgentsActionsCreator extends ActionCreatorBase {
    constructor() {
        super();
    }

    public static getKey(): string {
        return ActionCreatorKeys.AgentsActionsCreator;
    }

    public initialize(instanceId?: string) {
        this._actions = ActionsHubManager.GetActionsHub<AgentsActions>(AgentsActions, instanceId);
    }

    public updateAgentQueue(queueId: number) {
        this._actions.updateLoadingAgentSpecifications.invoke(true);
        AgentsSource.instance().getTaskAgentQueue(queueId)
            .then((queue: TaskAgentQueue) => {
                // update the queueId even if no queue is returned
                // in order to support <inherit from pipeline> which has an id of 0
                this._actions.updateAgentQueue.invoke(queue ? queue.id : queueId);
                return this.getAgentSpecifications(queue);
            })
            .catch(reason => {
                this.onRejected(reason);
            })
            .then(() => this._actions.updateLoadingAgentSpecifications.invoke(false));
    }

    public updateAgentSpecifications(queue: TaskAgentQueue) {
        this.getAgentSpecifications(queue);
    }

    public updateAgentSpecId(definition: IAgentSpecification) {
        this._actions.updateSelectedAgentSpecification.invoke(definition);
    }

    public refreshAgentQueue(): void {
        AgentsSource.instance().getTaskAgentQueues(true)
            .then((queues: TaskAgentQueue[]) => {
                this._actions.refreshAgentQueue.invoke(queues);
                PhaseCache.instance().updatePermissibleQueues(queues);
            })
            .catch(reason => {
                this.onRejected(reason);
            });
    }

    public updateAgentsQueueSection(queueId: number, agentSpecification: IAgentSpecification, forceUpdate?: boolean) {
        const payload: IActionQueuesPayload = {
            currentQueueId: queueId,
            currentAgentSpecification: agentSpecification,
            forceUpdate: forceUpdate
        } as IActionQueuesPayload;

        AgentsSource.instance().getTaskAgentQueues()
            .then(async (queues: TaskAgentQueue[]) => {
                payload.queues = queues;
                const currentQueue = queues.filter((queue: TaskAgentQueue) => queue.id === queueId)[0];
                return currentQueue ? Promise.resolve(currentQueue) : AgentsSource.instance().getTaskAgentQueue(queueId);
            })
            .then(async (queue: TaskAgentQueue) => {
                payload.agentQueueFromBuild = queue;
                return  AgentsSource.instance().getAgentSpecifications ?  AgentsSource.instance().getAgentSpecifications(queue) : [];
            })
            .then((agentSpecifications: IAgentSpecification[]) => {
                payload.agentSpecifications = agentSpecifications;
                this._actions.updateAgentsQueueSection.invoke(payload);
                PhaseCache.instance().updatePermissibleQueues(payload.queues);
            })
            .catch(reason => {
                this.onRejected(reason);
            });
    }

    public manageAgentClicked(): void {
        this._actions.manageAgent.invoke({});
    }

    private onRejected(reason: any) {
        if (reason && reason.message) {
            ActionCreatorManager.GetActionCreator<MessageHandlerActionsCreator>(MessageHandlerActionsCreator).addMessage(
                MessageParentKeyConstants.MainParentKey,
                reason.message,
                MessageBarType.error);
        }
    }

    private async getAgentSpecifications(queue: TaskAgentQueue): Promise<void> {

        return AgentsSource.instance().getAgentSpecifications(queue).then((agentSpecifications: IAgentSpecification[]) => {
            this._actions.updateAgentSpecifications.invoke(agentSpecifications ? agentSpecifications : []);
        })
        .catch(reason => {
            this.onRejected(reason);
        });
    }

    private _actions: AgentsActions;
}
